import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createBlock as _createBlock, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "portal" }
const _hoisted_2 = { class: "sidebar-header" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { key: 1 }
const _hoisted_5 = { class: "sidebar-nav" }
const _hoisted_6 = {
  key: 0,
  class: "badge"
}
const _hoisted_7 = {
  key: 0,
  class: "badge"
}
const _hoisted_8 = { class: "sidebar-footer" }
const _hoisted_9 = { class: "user-info" }
const _hoisted_10 = { class: "user-avatar" }
const _hoisted_11 = { class: "user-details" }
const _hoisted_12 = { class: "user-name" }
const _hoisted_13 = { class: "user-role" }
const _hoisted_14 = {
  key: 0,
  class: "role-impersonation"
}
const _hoisted_15 = { class: "theme-toggle" }
const _hoisted_16 = { class: "switch" }
const _hoisted_17 = ["checked"]
const _hoisted_18 = { class: "portal-content" }

import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore, assertIsLoggedIn } from '@/store/auth'
import { useThemeStore } from '@/store/theme'
import { api } from '@/composables/api'
import EditUserModal from '@/components/admin/EditUserModal.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'PortalView',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const themeStore = useThemeStore()

const sidebarOpen = ref(false)
const showUserSettings = ref(false)
const currentUserForEdit = ref<any>(null)
const schools = ref<any[]>([])
const submittingUser = ref(false)
const userError = ref('')

const authUser = computed(() => authStore.authUser)
const selectedImpersonationRole = ref<string | null>(null)

// Check if user is actually root (for dropdown visibility)
const isRootUser = computed(() => {
  return (authStore.actualRole || authStore.authUser.role) === 'root'
})

// Permission checks now use authUser.role directly (which is overridden during impersonation)
const isAdmin = computed(() => {
  return authStore.authUser && authStore.authUser.isAdmin()
})
const isAdminOrMaintainer = computed(() => {
  return authStore.authUser && authStore.authUser.isAdminOrMaintainer()
})

const handleRoleChange = () => {
  authStore.setImpersonatedRole(selectedImpersonationRole.value)
}

const currentTab = computed(() => {
  const path = route.path
  if (path.includes('/dashboard')) return 'dashboard'
  if (path.includes('/admin')) return 'admin'
  if (path.includes('/requests')) return 'requests'
  if (path.includes('/inventory')) return 'inventory'
  return 'dashboard'
})

const userInitials = computed(() => {
  const name = authUser.value.username || 'U'
  return name.charAt(0).toUpperCase()
})

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}

const closeSidebar = () => {
  sidebarOpen.value = false
}

const closeSidebarOnMobile = () => {
  if (window.innerWidth < 768) {
    closeSidebar()
  }
}

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

// User settings functions
const loadSchools = async () => {
  try {
    const response = await api.get('/schools')
    schools.value = response.data || response || []
  } catch (error) {
    console.error('Error loading schools:', error)
    schools.value = []
  }
}

const openUserSettings = () => {
  userError.value = ''
  // Create a copy of the current user for editing
  currentUserForEdit.value = {
    ...authUser.value,
    _id: authUser.value._id || authUser.value.id,
    id: authUser.value._id || authUser.value.id
  }
  showUserSettings.value = true
}

const closeUserSettings = () => {
  showUserSettings.value = false
  userError.value = ''
  setTimeout(() => {
    currentUserForEdit.value = null
  }, 300)
}

const updateCurrentUser = async (userData: any) => {
  submittingUser.value = true
  userError.value = ''

  try {
    const userId = authUser.value._id || authUser.value.id
    await api.put(`/users/${userId}`, userData)

    // Refresh the current user data
    await authStore.fetchAuthUser()

    closeUserSettings()

    // Show success message (you could add a toast/snackbar here)
    console.log('User settings updated successfully!')
  } catch (error: any) {
    console.error('Error updating user:', error)
    if (error.response?.status === 403) {
      userError.value = 'You do not have permission to update this information.'
    } else if (error.response?.status === 409) {
      userError.value = 'A user with that username or email already exists.'
    } else {
      userError.value = error.response?.data?.message || 'Failed to update user settings. Please try again.'
    }
  } finally {
    submittingUser.value = false
  }
}

onMounted(async () => {
  // Initialize theme
  themeStore.initTheme()

  // Check authentication
  const isLoggedIn = await assertIsLoggedIn()
  if (!isLoggedIn) {
    router.push('/login')
  }

  // Load schools for user settings
  await loadSchools()

  // Fetch pending users count if admin or maintainer
  if (isAdminOrMaintainer.value) {
    await authStore.fetchPendingUsersCount()
  }

  // Fetch school requests count for badge
  await authStore.fetchSchoolRequestsCount()

  // Auto-open sidebar on desktop
  if (window.innerWidth >= 768) {
    sidebarOpen.value = true
  }
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("aside", {
      class: _normalizeClass(["sidebar", { open: sidebarOpen.value }])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _cache[2] || (_cache[2] = _createElementVNode("img", {
          src: "/assets/img/logo-trans.png",
          alt: "Logo",
          class: "sidebar-logo"
        }, null, -1)),
        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "logo-text" }, [
          _createElementVNode("span", { class: "logo-space" }, "Space Coast"),
          _createElementVNode("span", { class: "logo-stem" }, "STEM")
        ], -1)),
        _createElementVNode("button", {
          class: "sidebar-toggle",
          onClick: toggleSidebar
        }, [
          (sidebarOpen.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "✕"))
            : (_openBlock(), _createElementBlock("span", _hoisted_4, "☰"))
        ])
      ]),
      _createElementVNode("nav", _hoisted_5, [
        _createVNode(_component_router_link, {
          to: "/dashboard",
          class: _normalizeClass(["nav-item", { active: currentTab.value === 'dashboard' }]),
          onClick: closeSidebarOnMobile
        }, {
          default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
            _createElementVNode("span", { class: "nav-icon" }, "📊", -1),
            _createElementVNode("span", { class: "nav-label" }, "Dashboard", -1)
          ]))]),
          _: 1
        }, 8, ["class"]),
        (isAdminOrMaintainer.value)
          ? (_openBlock(), _createBlock(_component_router_link, {
              key: 0,
              to: "/admin",
              class: _normalizeClass(["nav-item", { active: currentTab.value === 'admin' }]),
              onClick: closeSidebarOnMobile
            }, {
              default: _withCtx(() => [
                _cache[5] || (_cache[5] = _createElementVNode("span", { class: "nav-icon" }, "👥", -1)),
                _cache[6] || (_cache[6] = _createElementVNode("span", { class: "nav-label" }, "Admin", -1)),
                (_unref(authStore).pendingUsersCount > 0)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_unref(authStore).pendingUsersCount), 1))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["class"]))
          : _createCommentVNode("", true),
        _createVNode(_component_router_link, {
          to: "/requests",
          class: _normalizeClass(["nav-item", { active: currentTab.value === 'requests' }]),
          onClick: closeSidebarOnMobile
        }, {
          default: _withCtx(() => [
            _cache[7] || (_cache[7] = _createElementVNode("span", { class: "nav-icon" }, "📋", -1)),
            _cache[8] || (_cache[8] = _createElementVNode("span", { class: "nav-label" }, "Requests", -1)),
            (_unref(authStore).schoolRequestsCount > 0)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(_unref(authStore).schoolRequestsCount), 1))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }, 8, ["class"]),
        _createVNode(_component_router_link, {
          to: "/inventory",
          class: _normalizeClass(["nav-item", { active: currentTab.value === 'inventory' }]),
          onClick: closeSidebarOnMobile
        }, {
          default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
            _createElementVNode("span", { class: "nav-icon" }, "💻", -1),
            _createElementVNode("span", { class: "nav-label" }, "Inventory", -1)
          ]))]),
          _: 1
        }, 8, ["class"])
      ]),
      _createElementVNode("div", _hoisted_8, [
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, _toDisplayString(userInitials.value), 1),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, _toDisplayString(authUser.value.username), 1),
            _createElementVNode("div", _hoisted_13, _toDisplayString(_unref(authStore).actualRole ? `Viewing as: ${authUser.value.role}` : authUser.value.role), 1)
          ]),
          _createElementVNode("button", {
            onClick: openUserSettings,
            class: "btn-settings",
            title: "User Settings"
          }, [...(_cache[10] || (_cache[10] = [
            _createElementVNode("span", null, "⚙️", -1)
          ]))])
        ]),
        (isRootUser.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
              _cache[12] || (_cache[12] = _createElementVNode("label", { class: "impersonation-label" }, "View as:", -1)),
              _withDirectives(_createElementVNode("select", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedImpersonationRole).value = $event)),
                onChange: handleRoleChange,
                class: "role-selector"
              }, [...(_cache[11] || (_cache[11] = [
                _createElementVNode("option", { value: null }, "Root", -1),
                _createElementVNode("option", { value: "admin" }, "Admin", -1),
                _createElementVNode("option", { value: "maintainer" }, "Maintainer", -1),
                _createElementVNode("option", { value: "user" }, "User", -1)
              ]))], 544), [
                [_vModelSelect, selectedImpersonationRole.value]
              ])
            ]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_15, [
          _cache[14] || (_cache[14] = _createElementVNode("span", { class: "theme-label" }, "Dark Mode", -1)),
          _createElementVNode("label", _hoisted_16, [
            _createElementVNode("input", {
              type: "checkbox",
              checked: _unref(themeStore).isDark,
              onChange: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(themeStore).toggleTheme && _unref(themeStore).toggleTheme(...args)))
            }, null, 40, _hoisted_17),
            _cache[13] || (_cache[13] = _createElementVNode("span", { class: "slider" }, null, -1))
          ])
        ]),
        _createElementVNode("button", {
          onClick: handleLogout,
          class: "btn-logout"
        }, [...(_cache[15] || (_cache[15] = [
          _createElementVNode("span", null, "🚪", -1),
          _createTextVNode(" Logout ", -1)
        ]))])
      ])
    ], 2),
    (sidebarOpen.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "sidebar-overlay",
          onClick: closeSidebar
        }))
      : _createCommentVNode("", true),
    _createElementVNode("main", _hoisted_18, [
      _createVNode(_component_router_view)
    ]),
    _createVNode(EditUserModal, {
      show: showUserSettings.value,
      user: currentUserForEdit.value,
      schools: schools.value,
      submitting: submittingUser.value,
      error: userError.value,
      "can-edit-role": isAdmin.value,
      "is-self-edit": true,
      onClose: closeUserSettings,
      onSubmit: updateCurrentUser
    }, null, 8, ["show", "user", "schools", "submitting", "error", "can-edit-role"])
  ]))
}
}

})